import React, { useState, useEffect, useRef, useContext } from "react";
import { Button, Box, Typography, CircularProgress } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import { WidgetContext } from "src/contexts/WidgetContext";
import { useAuth0 } from "@auth0/auth0-react";
import CreditsUi from "../CreditsUi/CreditsUi";
import FreeCreditsUi from "../CreditsUi/FreeCreditsUi";
import CreditsValueButtons from "../CreditsUi/CreditsValueButtons";
import { isMobileWebBrowser, mobile } from "src/utils/platformUtils";
import { frontEnvNumber, subscribeFrontRuntimeConfig } from "src/services/runtimeFrontConfig";
import {
  buttonContentIconSlotSx,
  buttonContentInnerSx,
  buttonContentLabelSx,
  buttonContentRowGridSx,
  buttonContentTwoLinePrimarySx,
  buttonContentTwoLineSecondarySx,
} from "./buttonContentLayout";

interface AnimatedButtonProps {
  icon: string | React.ReactNode;
  label: string;
  onClick?: (model?: "RotoGPT 5.0" | "RotoGPT FREE") => void;
  isLoadingStart?: boolean;
  isLoadingNews?: boolean;
  // isLoadingRankings?: boolean;
  isLoadingAllRankings?: boolean;
  isLoadingWaiver?: boolean;
  isStreamActive?: boolean;
  isLoadingRosters?: boolean;
  isLoadingRundown?: boolean;
  isLoadingDraftAdvice?: boolean;
  hasLoadingStarted?: boolean;
  selectedPartner: string;
  signal?: AbortSignal;
  startData?: any;
  newsData?: any;
  // rankingsData?: any;
  allRankingsData?: any;
  waiverData?: any;
  rostersData?: any;
  rundownData?: any;
  DraftAdviceData?: any;
  onRetry?: () => void;
  hasError?: boolean;
  errorMessage?: string;
  commonLeagueDataFailed?: boolean;
  returnedFromBackground?: boolean;
  errorMessageNoDraft?: boolean;
  isStartupMode?: boolean;
  /** True only when common league data includes draft_order_set explicitly as false/0; missing field => false (Draft Advice stays enabled for that rule). */
  isDraftOrderSetExplicitlyFalse?: boolean;
  /** When true (common league: user_has_drafted_players), Rosters and Trades bypass startup_rookie permanent disable. */
  isUserHasDraftedPlayers?: boolean;
  /** When true, common league data is loaded for the selected team (used with isDraftOrderSetExplicitlyFalse for Draft Advice). */
  commonLeagueDataReadyForDraftOrder?: boolean;
  isLeagueTypeRedraft?: boolean;
  /** Positive: true when draft_order_set is explicitly 1/true in common league data. */
  isDraftOrderSet?: boolean;
  /** League status from common league data (pre_draft, drafting, post_draft, in_season, post_season, complete). */
  leagueStatus?: string;
  selectedTeamInfo?: { platformId: string; teamName: string; leagueId: string };
  waiverStreamState?: { status: string; thought: string; isStreamActive: boolean; isGeneratingResult?: boolean; teamKey: string | null };
  startStreamState?: { status: string; thought: string; isStreamActive: boolean; isGeneratingResult?: boolean; teamKey: string | null };
  rundownStreamState?: { status: string; thought: string; isStreamActive: boolean; isGeneratingResult?: boolean; teamKey: string | null };
  DraftAdviceStreamState?: { status: string; thought: string; isStreamActive: boolean; isGeneratingResult?: boolean; teamKey: string | null };
  isAnalysisPending?: boolean;
  isReadyForManualTrigger?: boolean;
  estimatedTimes?: { waiver: number; start: number; rundown: number; DraftAdvice?: number; news: number; rankings: number; rosters: number } | null;
  // NEW: Optional manual progress target (0-100) for stream-driven progress
  manualProgressTarget?: number;
  isCreditsAnimating?: boolean;
  creditsBoxRef?: React.RefObject<HTMLDivElement>;
  userSubscription?: string;
  /** When false (backend disabled), button stays visible but is not clickable and label shows "{label} Coming this Fall". */
  endpointEnabled?: boolean;
  /**
   * Un-Synced only: agent needs a real synced league.
   * Separate from isPermanentError so existing platform error UX is untouched.
   */
  requiresSyncedLeague?: boolean;
}


// function to create a two line button label
function twoLineButtonLabel(primary: string, secondary: string): string {
  return `${primary}\n${secondary}`;
}

function getPermanentNotAvailableLabel(label: string, teamName: string): string {
  // if (label === "Team Analysis") return "Team Analysis Not Available";
  // if (label === "Draft Advice") return "Draft Not Available";
  // if (label === "Rankings") return "Rankings Not Available";
  // return `${label} Not Available`;

  // return `${label} Live Post-Draft`;
  // return `${label} \n ${teamName} Error`;
  return twoLineButtonLabel(label, `${teamName} Error`);
}

type ResolveAnimatedButtonLabelParams = {
  label: string;
  displayLabel: string;
  isPermanentError: boolean;
  requiresSyncedLeague: boolean;
  isDraftOrderSetDisabled: boolean;
  isStartupDisabled: boolean;
  isUserHasDraftedPlayers: boolean;
  isStartupMode: boolean;
  isDraftOrderSet: boolean;
  isLeagueTypeRedraft: boolean;
  leagueStatus: string;
  selectedTeamInfo: { platformId: string; teamName: string; leagueId: string };
};

//FUNCTION TO RENDER MESSAGE IN BUTTONS
function resolveAnimatedButtonLabel({
  label,
  displayLabel,
  isPermanentError,
  requiresSyncedLeague,
  isDraftOrderSetDisabled,
  isStartupDisabled,
  isUserHasDraftedPlayers,
  isStartupMode,
  isDraftOrderSet,
  isLeagueTypeRedraft,
  leagueStatus,
  selectedTeamInfo,
}: ResolveAnimatedButtonLabelParams): string {

  // Un-Synced blocked agents: dedicated copy (do not reuse permanent-error paths)
  if (requiresSyncedLeague && label !== "News") {
    return twoLineButtonLabel(label, "Sync A League");
  }

  // draft_order_set explicitly 0/false: Draft Advice only (all league types/statuses)
  if (isDraftOrderSetDisabled) {
    return twoLineButtonLabel(label, "Set Draft Order");
  }

  // ── Scenario1: New redraft - No Draft Order (pre-draft) ─────────────────────
  // startup_rookie=1, league_type=Redraft, draft_order_set=0, league_status=pre_draft
  const isNewRedraftNoDraftOrderPreDraft =
    isStartupMode && isLeagueTypeRedraft && !isDraftOrderSet && leagueStatus === 'pre_draft';

  if (isNewRedraftNoDraftOrderPreDraft && isPermanentError && label !== "Articles") {
    if (label === "Start/Sit") return twoLineButtonLabel(label, "Live In August");
    return twoLineButtonLabel(label, "Live Post-Draft");
  }

  // ── Scenario2: New redraft - Draft Order Set (pre-draft) ────────────────────
  // startup_rookie=1, league_type=Redraft, draft_order_set=1, league_status=pre_draft
  const isNewRedraftDraftOrderSetPreDraft =
    isStartupMode && isLeagueTypeRedraft && isDraftOrderSet && leagueStatus === 'pre_draft';

  if (isNewRedraftDraftOrderSetPreDraft && isPermanentError && label !== "Articles") {
    if (label === "Start/Sit") return twoLineButtonLabel(label, "Live In August");
    return twoLineButtonLabel(label, "Live Post-Draft");

  }

  // ── Scenario3: New redraft (mid-draft) ─────────────────────────────────────
  // startup_rookie=1, league_type=Redraft, league_status=drafting
  const isNewRedraftMidDraft =
    isStartupMode && isLeagueTypeRedraft && leagueStatus === 'drafting';

  if (isNewRedraftMidDraft && isPermanentError && label !== "Articles") {
    if (label === "Start/Sit") return twoLineButtonLabel(label, "Live In August");
    return twoLineButtonLabel(label, "Live Post-Draft");
  }

  // ── Scenario4: New redraft (post-draft) ────────────────────────────────────
  // startup_rookie=1, league_type=Redraft, league_status=post_draft
  const isNewRedraftPostDraft =
    isStartupMode && isLeagueTypeRedraft && leagueStatus === 'post_draft';

  if (isNewRedraftPostDraft && isPermanentError && label === "Draft Advice") {
    return twoLineButtonLabel(label, "Live Next Season");
  }

  // ── Scenario5: New dynasty - No Draft Order (pre-draft) ────────────────────
  // startup_rookie=1, league_type=Dynasty (not Redraft), draft_order_set=0, league_status=pre_draft
  const isNewDynasty = isStartupMode && !isLeagueTypeRedraft;
  const isNewDynastyNoDraftOrderPreDraft =
    isNewDynasty && !isDraftOrderSet && leagueStatus === 'pre_draft';

  if (isNewDynastyNoDraftOrderPreDraft && isPermanentError && label !== "Articles") {
    if (label === "Start/Sit") return twoLineButtonLabel(label, "Live In August");
    return twoLineButtonLabel(label, "Live Post-Draft");
  }

  // ── Scenario6: New dynasty - Draft Order Set (pre-draft) ─────────────────────
  const isNewDynastyDraftOrderSetPreDraft =
    isNewDynasty && isDraftOrderSet && leagueStatus === 'pre_draft';

  if (isNewDynastyDraftOrderSetPreDraft && isPermanentError && label !== "Articles") {
    if (label === "Start/Sit") return twoLineButtonLabel(label, "Live In August");
    return twoLineButtonLabel(label, "Live Post-Draft");
  }

  // ── Scenario7: New dynasty (mid-draft) ───────────────────────────────────────
  const isNewDynastyMidDraft = isNewDynasty && leagueStatus === 'drafting';

  if (isNewDynastyMidDraft && isPermanentError && label !== "Articles") {
    if (label === "Start/Sit") return twoLineButtonLabel(label, "Live In August");
    return twoLineButtonLabel(label, "Live Post-Draft");
  }

  // if there is a permanent error (like common league data failed), show the Not Available message
  if (isPermanentError) {
    return getPermanentNotAvailableLabel(label, selectedTeamInfo?.platformId);
  }

  //if everything is ok, show the original label name
  return displayLabel;
}

const AnimatedButton: React.FC<AnimatedButtonProps> = ({
  icon,
  label,
  onClick,
  isLoadingStart,
  isLoadingNews,
  // isLoadingRankings,
  isLoadingAllRankings,
  isLoadingWaiver,
  isStreamActive,
  isLoadingRosters,
  isLoadingRundown,
  isLoadingDraftAdvice,
  hasLoadingStarted = false,
  selectedPartner,
  startData,
  newsData,
  // rankingsData,
  allRankingsData,
  waiverData,
  rostersData,
  rundownData,
  DraftAdviceData,
  onRetry,
  hasError,
  errorMessage,
  commonLeagueDataFailed,
  returnedFromBackground,
  errorMessageNoDraft,
  isStartupMode,
  isDraftOrderSetExplicitlyFalse = false,
  isUserHasDraftedPlayers = false,
  commonLeagueDataReadyForDraftOrder = false,
  isLeagueTypeRedraft,
  isDraftOrderSet = false,
  leagueStatus = '',
  selectedTeamInfo,
  waiverStreamState,
  startStreamState,
  rundownStreamState,
  DraftAdviceStreamState,
  isAnalysisPending,
  isReadyForManualTrigger,
  estimatedTimes,
  manualProgressTarget,
  isCreditsAnimating,
  creditsBoxRef,
  userSubscription,
  endpointEnabled,
  requiresSyncedLeague = false,
}) => {
  const [, setFrontRuntimeBump] = useState(0);
  useEffect(() => subscribeFrontRuntimeConfig(() => setFrontRuntimeBump((n) => n + 1)), []);

  const endpointAvailable = endpointEnabled !== false;
  const comingThisFallLabel = label === "Team Analysis" ? 'Analysis' : label === "Draft Advice" ? 'Draft' : label;
  // const displayLabel = !endpointAvailable ? twoLineButtonLabel(label, 'Coming Soon') : label;
  const displayLabel = !endpointAvailable ? (label === "Start/Sit" ? twoLineButtonLabel(label, "Live In August") : twoLineButtonLabel(label, 'Coming Soon')) : label;
  // States to handle the progress of the animation and the completion status
  // const [progress, setProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  // Refs to handle animation and start time
  const animationFrameRef = useRef<number>();
  const startTimeRef = useRef<number>(Date.now());
  const [showRetry, setShowRetry] = useState(false);
  const [hasRetried, setHasRetried] = useState(false);
  const isManualLabel = ["Waivers", "Start/Sit", "Team Analysis", "Draft Advice"].includes(label);

  const isMobile = isMobileWebBrowser() || mobile;

  // Get authId from user auth0
  const { user } = useAuth0();
  const authId = user?.sub;

  // Generate unique key based on team/platform/section
  const progressKey = `${authId}-${selectedTeamInfo?.platformId}-${selectedTeamInfo?.leagueId}-${label.toLowerCase()}`;

  const { getButtonProgress, updateButtonProgress, isLoggedInAuth0ExperienceGlobal, credits, freeCredits, aiSettings } = useContext(WidgetContext);
  // Audio OFF: same flag sent as audio_settings in the stream payload (early unlock + ready visuals).
  const isAudioOff = aiSettings?.audio === false;
  // Get persisted progress from the context
  const persistedProgress = getButtonProgress(progressKey);

  // CRITICAL: For manual buttons, always start at 0% when component mounts
  // This ensures buttons don't show 100% when switching teams with cached data
  const initialProgress = isManualLabel ? 0 : persistedProgress;
  const [progress, setProgress] = useState(initialProgress);

  // Update context when progress changes
  useEffect(() => {
    updateButtonProgress(progressKey, progress);
  }, [progress, progressKey]);

  // Mirror external resets (e.g. Refresh League Data calls updateButtonProgress(key, 0)) into local state.
  // Otherwise local progress stays at 100 while context is 0 — loader shows full bar at stream start.
  const contextStoredProgress = getButtonProgress(progressKey);
  useEffect(() => {
    if (!isManualLabel) return;
    if (contextStoredProgress !== 0) return;
    setProgress((p) => (p > 0 ? 0 : p));
    setIsCompleted(false);
  }, [contextStoredProgress, progressKey, isManualLabel]);

  // Permanent UI: disabled + "Not Available" (this surface does not offer retry for these cases).
  // - commonLeagueDataFailed / errorMessageNoDraft: all affected buttons; Articles and News excluded below.
  // - startup_rookie: same as a global block for the grid except Draft Advice + Articles + Rankings + News; Rosters/Trades also bypass when user_has_drafted_players is true.
  // - draft_order_set: no longer blocks Draft Advice (leagues without draft order can still open the endpoint).
  // - league_type redraft: only Draft Advice gets the block (isLeagueTypeRedraftDisabled).
  const isNewsAlwaysEnabled = label === "News";
  const isStartupDisabled =
    isStartupMode &&
    label !== "Draft Advice" &&
    label !== "Articles" &&
    label !== "News" &&
    label !== "Rankings" &&
    !(isUserHasDraftedPlayers && (label === "Rosters" || label === "Trades"));
  // Draft Advice stays enabled regardless of draft_order_set (was: isDraftOrderSetExplicitlyFalse && label === "Draft Advice" && commonLeagueDataReadyForDraftOrder).
  const isDraftOrderSetDisabled = false;

  // Draft Advice is disabled when league type is redraft
  // const isLeagueTypeRedraftDisabled = isLeagueTypeRedraft && label === "Draft Advice";

  const isPermanentError =
    label !== "Articles" &&
    label !== "News" &&
    !!(commonLeagueDataFailed || errorMessageNoDraft || isStartupDisabled || isDraftOrderSetDisabled);
  const isLeagueStateBlocked = !isNewsAlwaysEnabled && (isPermanentError || requiresSyncedLeague);
  // use disabled gradient if there is a permanent error or if the button is not available and the label is not Articles
  const useDisabledGradient =
    isLeagueStateBlocked ||
    (!endpointAvailable && label !== "Articles");

  const buttonLabelText = resolveAnimatedButtonLabel({
    label,
    displayLabel,
    isPermanentError,
    requiresSyncedLeague,
    isDraftOrderSetDisabled,
    isStartupDisabled,
    isUserHasDraftedPlayers,
    isStartupMode: !!isStartupMode,
    isDraftOrderSet,
    isLeagueTypeRedraft: !!isLeagueTypeRedraft,
    leagueStatus,
    selectedTeamInfo,
  });

  // Split the button label text into two lines if it contains a newline character
  const buttonLabelLines = buttonLabelText.includes('\n')
    ? buttonLabelText.split('\n')
    : null;

  // DEBUG: Log for manual buttons when permanent error should be true
  // if (isManualLabel && commonLeagueDataFailed) {
  //   console.log(`[AnimatedButton ${label}] isPermanentError:`, isPermanentError, "commonLeagueDataFailed:", commonLeagueDataFailed, "errorMessageNoDraft:", errorMessageNoDraft);
  // }

  const initialColor = "#111111";
  const finalColor = "#222222";

  // Set the estimated times for each endpoint (in milliseconds)
  // Use times from backend if available, otherwise use default values
  const defaultEstimatedTimes = {
    waiver: 70000, //70 s
    start: 70000, //70 s
    rundown: 70000, //70 s
    DraftAdvice: 70000, //70 s
    news: 15000, //15 s
    rankings: 15000, //15 s
    rosters: 17000, //17 s
  };

  // const timesToUse = estimatedTimes || defaultEstimatedTimes;
  const timesToUse = estimatedTimes || defaultEstimatedTimes;

  // Identify which loading is active and its estimated time
  const getEstimatedDuration = () => {
    if (isLoadingStart) return timesToUse.start;
    if (isLoadingWaiver) return timesToUse.waiver;
    if (isLoadingNews) return timesToUse.news;
    // if (isLoadingRankings) return timesToUse.rankings;
    if (isLoadingAllRankings) return timesToUse.rankings;
    if (isLoadingRosters) return timesToUse.rosters;
    if (isLoadingRundown) return timesToUse.rundown;
    if (isLoadingDraftAdvice) return timesToUse.DraftAdvice ?? timesToUse.rundown;
  };

  // Variable that indicates if any operation is in the process of being loaded
  // const currentLoading = isLoadingStart || isLoadingNews || isLoadingRankings || isLoadingRookiesRankings || isLoadingWaiver || isLoadingRosters || isLoadingRundown || isLoadingDraftAdvice;
  const currentLoading = isLoadingStart || isLoadingNews || isLoadingAllRankings || isLoadingWaiver || isLoadingRosters || isLoadingRundown || isLoadingDraftAdvice;

  // Team key actual (plataforma + league) para este botón
  const currentTeamKey = selectedTeamInfo ? `${selectedTeamInfo.platformId}-${selectedTeamInfo.leagueId}` : null;

  // IMPORTANT:
  // Para Waivers / Start / Rundown, el loader SOLO debe considerar el loading
  // si el streamState.teamKey coincide con el team actual.
  // Así evitamos que el loader del team 1 “se meta” en el team 2.
  const loadingForProgress =
    label === "Waivers"
      ? !!(isLoadingWaiver && waiverStreamState?.teamKey === currentTeamKey)
      : label === "Start/Sit"
        ? !!(isLoadingStart && startStreamState?.teamKey === currentTeamKey)
        : label === "Team Analysis"
          ? !!(isLoadingRundown && rundownStreamState?.teamKey === currentTeamKey)
          : label === "Draft Advice"
            ? !!(isLoadingDraftAdvice && DraftAdviceStreamState?.teamKey === currentTeamKey)
            : label === "Trades"
              ? currentLoading || !!isAnalysisPending
              : currentLoading;

  // This is the new source of truth. It's true if stream content (status/thought) exists AND belongs to the current team.
  const isStreamActiveForThisTeam =
    (waiverStreamState?.teamKey === currentTeamKey && waiverStreamState?.isStreamActive) ||
    (startStreamState?.teamKey === currentTeamKey && startStreamState?.isStreamActive) ||
    (rundownStreamState?.teamKey === currentTeamKey && rundownStreamState?.isStreamActive) ||
    (DraftAdviceStreamState?.teamKey === currentTeamKey && DraftAdviceStreamState?.isStreamActive);

  const activeManualStreamState =
    label === "Waivers"
      ? waiverStreamState
      : label === "Start/Sit"
        ? startStreamState
        : label === "Team Analysis"
          ? rundownStreamState
          : label === "Draft Advice"
            ? DraftAdviceStreamState
            : null;

  // This endpoint's SSE still open (incl. late thoughts / audio chunks after text_chunk).
  const thisManualStreamActive =
    !!activeManualStreamState &&
    activeManualStreamState.teamKey === currentTeamKey &&
    activeManualStreamState.isStreamActive;

  // Helper: Check if there's valid cached data for the current team (manual buttons only)
  const hasValidDataForCurrentTeam = () => {
    if (!isManualLabel) return false;

    return (
      (label === "Waivers" &&
        waiverData?.waiver_analysis &&
        waiverData?.context?.platformId === selectedTeamInfo?.platformId &&
        waiverData?.context?.leagueId === selectedTeamInfo?.leagueId) ||
      (label === "Start/Sit" &&
        startData?.start_analysis &&
        startData?.context?.platformId === selectedTeamInfo?.platformId &&
        startData?.context?.leagueId === selectedTeamInfo?.leagueId) ||
      (label === "Team Analysis" &&
        rundownData?.rundown_analysis &&
        rundownData?.context?.platformId === selectedTeamInfo?.platformId &&
        rundownData?.context?.leagueId === selectedTeamInfo?.leagueId) ||
      (label === "Draft Advice" &&
        DraftAdviceData?.rundown_analysis &&
        DraftAdviceData?.context?.platformId === selectedTeamInfo?.platformId &&
        DraftAdviceData?.context?.leagueId === selectedTeamInfo?.leagueId)
    );
  };

  // Button disabled until the loading is complete
  const isDisabled =
    !endpointAvailable ||
    (!isNewsAlwaysEnabled && isPermanentError) || // Disabled for permanent errors
    (!isNewsAlwaysEnabled && requiresSyncedLeague) || // Un-Synced: blocked agents until a real league is synced
    (!isNewsAlwaysEnabled && !hasLoadingStarted) ||
    (label === "News" && isLoadingNews && !hasError) ||
    (label === "Waivers" && isLoadingWaiver && !isStreamActiveForThisTeam && !hasError) ||
    // (label === "Waivers" && isLoadingWaiver && !hasError) ||
    // (label === "Rankings" && isLoadingRankings && !hasError) ||
    (label === "Rankings" && isLoadingAllRankings && !hasError) ||
    (label === "Start/Sit" && isLoadingStart && !isStreamActiveForThisTeam && !hasError) ||
    // (label === "Start/Sit" && isLoadingStart && !hasError) ||
    (label === "Trades" && ((isLoadingRosters && !hasError) || isAnalysisPending)) ||
    (label === "Rosters" && isLoadingRosters && !hasError) ||
    (label === "Team Analysis" && isLoadingRundown && !isStreamActiveForThisTeam && !hasError) ||
    (label === "Draft Advice" && isLoadingDraftAdvice && !isStreamActiveForThisTeam && !hasError);

  // CRITICAL: Check if ANY loading is active (prevents premature white/gray when returning from ad)
  const isAnyLoadingActive = isLoadingWaiver || isLoadingStart || isLoadingRundown || isLoadingDraftAdvice;

  const shouldBeWhite =
    endpointAvailable &&
    !isPermanentError &&
    !hasError && // This is the main fix. An errored button should never be styled as "complete".
    // CRITICAL: Don't show white/gray if ANY loading is active (prevents premature completion after ad)
    !isAnyLoadingActive &&
    ((isCompleted &&
      !currentLoading &&
      ((label === "News" && newsData) ||
        (label === "Articles" && isCompleted) ||
        (label === "Waivers" && waiverData?.waiver_analysis) ||
        // (label === "Rankings" && rankingsData) ||
        (label === "Rankings" && allRankingsData) ||
        (label === "Start/Sit" && startData?.start_analysis) ||
        (label === "Trades" && rostersData && isReadyForManualTrigger) ||
        (label === "Rosters" && rostersData) ||
        (label === "Team Analysis" && rundownData?.rundown_analysis) ||
        (label === "Draft Advice" && DraftAdviceData?.rundown_analysis))) ||
      // NEW: streaming active with partial content - button becomes white immediately
      (isStreamActiveForThisTeam &&
        ((label === "Waivers" && waiverData?.waiver_analysis) ||
          (label === "Start/Sit" && startData?.start_analysis) ||
          (label === "Team Analysis" && rundownData?.rundown_analysis) ||
          (label === "Draft Advice" && DraftAdviceData?.rundown_analysis))));


  // Sync "ready" detection for manual buttons: if this button has valid data for the current
  // team and nothing is actively loading/streaming it, force the ready (light) gradient.
  // Decouples the gradient from the async isCompleted/progress state to fix the race where the
  // button is clickable (credits badge hidden) but the gradient stays dark.
  // Audio OFF: allow ready gradient as soon as first text exists (do not wait for SSE end).
  const manualReadyWithData =
    isManualLabel &&
    hasValidDataForCurrentTeam() &&
    !isAnalysisPending &&
    (isAudioOff || (!loadingForProgress && !thisManualStreamActive)) &&
    endpointAvailable &&
    !isPermanentError &&
    !hasError;

  // CRITICAL: Calculate backgroundColor - when isPermanentError is true, use finalColor (gray)
  // const backgroundColor = isPermanentError ? finalColor : shouldBeWhite ? finalColor : initialColor;
  const backgroundColor = (isLeagueStateBlocked || shouldBeWhite || manualReadyWithData) ? finalColor : initialColor;
  // Figma-style: not ready = dark gradient fading to page bg (#000000); ready/100% = lighter gradient
  const gradientTopColor = backgroundColor === initialColor ? "#1f1f1f" : "#555555";
  const gradientBottomColor = backgroundColor === initialColor ? "#111111" : "#333333";
  // Same gradient as "ready" state so the progress bar has gradient and matches at 100%
  const loaderGradient = "linear-gradient(180deg, #555555, #111111)";
  // Reset the state when the selectedPartner changes or when any loading changes to false
  // useEffect(() => {
  //   setProgress(0);
  //   setIsCompleted(false);
  //   startTimeRef.current = Date.now();
  // }, [selectedPartner, currentLoading]);

  // Ref to track previous progressKey for team change detection
  const prevProgressKeyRef = useRef(progressKey);

  useEffect(() => {
    // CRITICAL: Always reset progress to 0 when team changes (progressKey changes)
    // This prevents showing progress from previous team
    if (progressKey !== prevProgressKeyRef.current) {
      if (isManualLabel) {
        setProgress(0);
        setIsCompleted(false);
        startTimeRef.current = Date.now();
        // Clear persisted progress for the old key
        updateButtonProgress(prevProgressKeyRef.current, 0);
      }
      prevProgressKeyRef.current = progressKey;
    }
  }, [progressKey, isManualLabel, updateButtonProgress]);

  // PRECISE: Reset progress to 0 when isAnalysisPending becomes true for manual buttons
  // This ensures "Analyzing..." always shows with 0% progress
  useEffect(() => {
    const isManualLabel = ["Waivers", "Start/Sit", "Team Analysis", "Draft Advice"].includes(label);
    if (isManualLabel && isAnalysisPending) {
      setProgress(0);
      setIsCompleted(false);
    }
    // For Trades, also reset progress when analysis is pending
    if (label === "Trades" && isAnalysisPending) {
      setProgress(0);
      setIsCompleted(false);
    }
  }, [isAnalysisPending, label]);

  // useEffect to handle the loading animation
  useEffect(() => {
    if (useDisabledGradient) {
      setProgress(100);
      setIsCompleted(true);
      return;
    }

    // If we have manual progress for a manual button, skip time-based animation
    const useManualProgress = isManualLabel && typeof manualProgressTarget === "number";

    if (loadingForProgress && !useManualProgress) {
      // For Trades: only reset when rosters starts; don't reset when rosters finishes but super analysis still pending (same as ChatBot)
      const shouldReset = label !== "Trades" || isLoadingRosters;
      if (shouldReset) {
        setProgress(0);
        setIsCompleted(false);
        startTimeRef.current = Date.now();
        updateButtonProgress(progressKey, 0);
      }

      // Animation function to be executed at every frame
      const animate = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTimeRef.current;
        const estimatedDuration = getEstimatedDuration();

        // CRITICAL: For manual buttons, always start from 0, never use persisted progress
        // This ensures fresh loads always start at 0% even if there's cached data
        // const baseProgress = isManualLabel ? 0 : hasPersistedProgress ? getButtonProgress(progressKey) : 0;
        const baseProgress = 0;
        // Calculate the progress (maximum 95% until the end of the load)
        // For Trades, also cap at 95% until super analysis completes
        const maxProgress = label === "Trades" && isAnalysisPending ? 95 : 95;
        // const nextProgress = Math.min(baseProgress + (elapsed / estimatedDuration) * 100, 95);
        const nextProgress = Math.min(baseProgress + (elapsed / estimatedDuration) * 100, maxProgress);

        setProgress(nextProgress);
        //Continue the animation as long as there is a load
        if (loadingForProgress) {
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    } else if (!loadingForProgress && progress > 0) {
      // CRITICAL: For manual buttons, only complete to 100% if there's valid data for current team
      // This prevents "false 100%" after refresh when data was cleared
      if (isManualLabel) {
        if (hasValidDataForCurrentTeam()) {
          setProgress(100);
          setIsCompleted(true);
        } else {
          // Refresh/cleanup left us without valid data → reset to 0% (idle state)
          setProgress(0);
          setIsCompleted(false);
        }
      } else if (label === "Trades") {
        // For Trades, only complete to 100% when super analysis is complete
        if (isReadyForManualTrigger) {
          setProgress(100);
          setIsCompleted(true);
        } else {
          // Keep progress at 95% max until super analysis completes
          setProgress(Math.min(progress, 95));
          setIsCompleted(false);
        }
      } else {
        // For non-manual buttons, use original logic
        setProgress(100);
        setIsCompleted(true);
      }
    }

    // Cleaning useEffect: cancel the animation when the component is dismounted or loading state changes
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [loadingForProgress, isPermanentError, label, progressKey, selectedTeamInfo, manualProgressTarget, isManualLabel, isAnalysisPending]);

  // NEW: Drive progress smoothly via manualProgressTarget (stream events → targets)
  useEffect(() => {
    const useManualProgress = isManualLabel && typeof manualProgressTarget === "number" && loadingForProgress;
    if (!useManualProgress) return;
    // Audio OFF + first text: jump to 100% (matches early click unlock). Audio ON unchanged.
    if (isAudioOff && hasValidDataForCurrentTeam()) {
      setProgress(100);
      setIsCompleted(true);
      return;
    }
    // Cap at 95% while stream is still active so disabled button does not look "done" at 100%.
    const progressCap = thisManualStreamActive ? 95 : 100;
    setProgress((prev) => Math.max(prev, Math.min(progressCap, Math.max(0, manualProgressTarget as number))));
  }, [manualProgressTarget, isManualLabel, loadingForProgress, thisManualStreamActive, isAudioOff, label, waiverData, startData, rundownData, DraftAdviceData, selectedTeamInfo]);

  // Ensure Waivers shows 100% when valid cached data exists (no load in progress)
  useEffect(() => {
    if (
      isManualLabel &&
      hasValidDataForCurrentTeam() &&
      !loadingForProgress && // no stream en curso
      !isAnalysisPending && // no “Analyzing...”
      !thisManualStreamActive
    ) {
      setProgress(100);
      setIsCompleted(true);
    }
  }, [label, waiverData, startData, rundownData, DraftAdviceData, selectedTeamInfo, loadingForProgress, isAnalysisPending, thisManualStreamActive]);

  // Modify the logic of when a button is completed
  useEffect(() => {
    const isManualLabel = ["Waivers", "Start/Sit", "Team Analysis", "Draft Advice"].includes(label);

    if (isManualLabel) {
      // For manual buttons, they are "complete" only if they have valid data for the current team
      // AND progress > 0 (meaning user has interacted or a load has occurred)
      const hasValidData =
        (label === "Waivers" &&
          waiverData?.waiver_analysis &&
          waiverData.context?.platformId === selectedTeamInfo?.platformId &&
          waiverData.context?.leagueId === selectedTeamInfo?.leagueId) ||
        (label === "Start/Sit" &&
          startData?.start_analysis &&
          startData.context?.platformId === selectedTeamInfo?.platformId &&
          startData.context?.leagueId === selectedTeamInfo?.leagueId) ||
        (label === "Team Analysis" &&
          rundownData?.rundown_analysis &&
          rundownData.context?.platformId === selectedTeamInfo?.platformId &&
          rundownData.context?.leagueId === selectedTeamInfo?.leagueId) ||
        (label === "Draft Advice" &&
          DraftAdviceData?.rundown_analysis &&
          DraftAdviceData.context?.platformId === selectedTeamInfo?.platformId &&
          DraftAdviceData.context?.leagueId === selectedTeamInfo?.leagueId);

      // CRITICAL: Only mark as completed if there's valid data AND progress > 0
      // This ensures buttons start at 0% when switching teams, even with cached data
      if (hasValidData && progress > 0) {
        setIsCompleted(true);
      } else {
        setIsCompleted(false);
      }
    } else if (label === "Trades") {
      // For Trades, only mark as completed when super analysis is complete
      if (isReadyForManualTrigger && !currentLoading && hasLoadingStarted) {
        const timer = setTimeout(() => {
          setIsCompleted(true);
        }, 500); // Small delay for the transition

        return () => clearTimeout(timer);
      } else {
        setIsCompleted(false);
      }
    } else {
      // For non-manual buttons (News, Rankings, Rosters, Articles), use original logic
      // Coming Soon (endpoint off): never auto-fill Articles to 100% when other sections finish loading
      if (!endpointAvailable) {
        setIsCompleted(false);
        if (label === "Articles") setProgress(0);
      } else if (!currentLoading && hasLoadingStarted) {
        const timer = setTimeout(() => {
          setIsCompleted(true);
          if (label === "Articles") setProgress(100);
        }, 500); // Small delay for the transition

        return () => clearTimeout(timer);
      } else {
        setIsCompleted(false);
        if (label === "Articles") setProgress(0);
      }
    }
  }, [currentLoading, hasLoadingStarted, label, waiverData, startData, rundownData, DraftAdviceData, selectedTeamInfo, progress, isReadyForManualTrigger, endpointAvailable]);

  //useEffect to handle the retry button
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (hasError) {
      timeoutId = setTimeout(() => {
        setShowRetry(true);
      }, 500);
    } else {
      setShowRetry(false);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [hasError]);

  // Reset hasRetried when error is resolved
  // useEffect(() => {
  //   if (!hasError) {
  //     setHasRetried(false);
  //   }
  // }, [hasError]);

  // // Reset hasRetried when partner changes
  // useEffect(() => {
  //   setHasRetried(false);
  // }, [selectedPartner]);

  // function to handle the click of the retry button
  const handleClick = () => {
    if (hasError) {
      if (!hasRetried) {
        setHasRetried(true);
        setProgress(0); // Set progress to 0 localy
        updateButtonProgress(progressKey, 0); // Set progress to 0 in the widget context
        startTimeRef.current = Date.now(); // Set the start time to the current time
        onRetry?.();
      }
    } else {
      onClick?.();
    }
  };

  // Dont show retry button if we came back from background
  // And DO NOT show retry for a permanent, non-retryable error.
  const showRetryButton = hasError && !isPermanentError && !returnedFromBackground;

  // Helper to render either an image icon (string) or a React icon component
  const manualLoading =
    label === "Waivers"
      ? isLoadingWaiver
      : label === "Start/Sit"
        ? isLoadingStart
        : label === "Team Analysis"
          ? isLoadingRundown
          : label === "Draft Advice"
            ? isLoadingDraftAdvice
            : false;

  // Use isStreamActive instead of isGeneratingResult - it's more precise (only true when text_chunk arrives)
  // const currentTeamKey = selectedTeamInfo ? `${selectedTeamInfo.platformId}-${selectedTeamInfo.leagueId}` : null;
  // For Waivers: require text + audio chunk (if audio chunks are sent) OR just text as fallback
  // Audio OFF: enable on first text_chunk (isAudioOff defined above from aiSettings.audio).
  const hasWaiverText = !!(waiverData?.waiver_analysis && waiverData.waiver_analysis.length > 0);
  const waiverAudioStatus = waiverData?.hasAudioChunk; // true / false / undefined
  const hasWaiverAudioChunk = waiverAudioStatus === true;
  const hasNoWaiverAudio = waiverAudioStatus === false; // Explicitly confirmed no audio after stream complete

  const hasStartText = !!(startData?.start_analysis && startData.start_analysis.length > 0);
  const startAudioStatus = startData?.hasAudioChunk; // true / false / undefined
  const hasStartAudioChunk = startAudioStatus === true;
  const hasNoStartAudio = startAudioStatus === false; // Explicitly confirmed no audio after stream complete

  const hasRundownText = !!(rundownData?.rundown_analysis && rundownData.rundown_analysis.length > 0);
  const rundownAudioStatus = rundownData?.hasAudioChunk; // true / false / undefined
  const hasRundownAudioChunk = rundownAudioStatus === true;
  const hasNoRundownAudio = rundownAudioStatus === false; // Explicitly confirmed no audio after stream complete

  const hasDraftAdviceText = !!(DraftAdviceData?.rundown_analysis && DraftAdviceData.rundown_analysis.length > 0);
  const DraftAdviceAudioStatus = DraftAdviceData?.hasAudioChunk;
  const hasDraftAdviceAudioChunk = DraftAdviceAudioStatus === true;
  const hasNoDraftAdviceAudio = DraftAdviceAudioStatus === false;

  const streamHasFirstText =
    label === "Waivers"
      ? hasWaiverText && (isAudioOff || hasWaiverAudioChunk || hasNoWaiverAudio)
      : label === "Start/Sit"
        ? hasStartText && (isAudioOff || hasStartAudioChunk || hasNoStartAudio)
        : label === "Team Analysis"
          ? hasRundownText && (isAudioOff || hasRundownAudioChunk || hasNoRundownAudio)
          : label === "Draft Advice"
            ? hasDraftAdviceText && (isAudioOff || hasDraftAdviceAudioChunk || hasNoDraftAdviceAudio)
            : false;

  // Block click while SSE is still in thought/status phase (before first result event).
  const isThoughtPhaseActive =
    !!activeManualStreamState &&
    activeManualStreamState.teamKey === currentTeamKey &&
    activeManualStreamState.isStreamActive &&
    !activeManualStreamState.isGeneratingResult;

  // Audio ON: keep blocking while SSE is open (wait for audio / complete).
  // Audio OFF: unlock once first text_chunk is present (do not wait for stream end).
  const manualStreamBlocksClick =
    manualLoading && (!streamHasFirstText || isThoughtPhaseActive || (!isAudioOff && thisManualStreamActive));

  // NEW: Specific loading flag for the current NON-MANUAL button
  const thisNonManualButtonIsLoading =
    !isManualLabel &&
    // ((label === "News" && isLoadingNews) || (label === "Rankings" && isLoadingRankings) || (label === "Rosters" && isLoadingRosters) || (label === "Trades" && isLoadingRosters));
    ((label === "News" && isLoadingNews) || (label === "Rankings" && isLoadingAllRankings) || (label === "Rosters" && isLoadingRosters) || (label === "Trades" && isLoadingRosters));

  // const isPendingState = isManualLabel ? isAnalysisPending : loadingForProgress;
  const isPendingState = isManualLabel
    ? isAnalysisPending
    : label === "Trades"
      ? isAnalysisPending || !hasLoadingStarted || (currentLoading && !thisNonManualButtonIsLoading)
      : !endpointAvailable
        ? false
        : !hasLoadingStarted || (currentLoading && !thisNonManualButtonIsLoading);
  // NEW: Detect when button is actively loading (stream active)
  // For manual buttons: only when stream is active, NOT when just waiting for analysis
  const isActivelyLoading = isManualLabel ? manualStreamBlocksClick : thisNonManualButtonIsLoading;

  //loading
  const isLoading = isManualLabel
    ? isAnalysisPending || manualStreamBlocksClick
    : label === "Trades"
      ? isAnalysisPending || loadingForProgress || isPendingState
      : loadingForProgress || isPendingState;

  // Render the icon
  const renderIcon = (withLargeSize: boolean) => {
    if (typeof icon === "string") {
      // Calculate opacity: 1 when actively loading (same as other buttons), 0.5 if pending, otherwise use existing logic
      const iconOpacity = !endpointAvailable ? 0.5 : isActivelyLoading ? 1 : isPendingState ? 0.5 : shouldBeWhite || !hasError ? 1 : 0.5;
      // Use brightness filter to darken white icons to gray when loading
      // const iconFilter = isLoading
      //   ? `grayscale(100%) brightness(0.5)` // Gray when loading (grayscale + darker)
      //   : `grayscale(${shouldBeWhite ? 0 : 100}%)`;
      const iconFilter = !endpointAvailable
        ? `grayscale(100%) brightness(0.9)`
        : isActivelyLoading
          ? `grayscale(100%) brightness(0.5)` // Gray when loading (matches #808080)
          : isPendingState
            ? `grayscale(100%) brightness(0.9)` // Lighter gray when waiting for analysis
            : isLeagueStateBlocked
              ? `grayscale(100%) brightness(0.5)` // Gray when error (matches #808080)
              : `grayscale(${shouldBeWhite ? 0 : 100}%)`;

      const iconSize = withLargeSize ? (label === "Draft Advice" ? "34px" : "40px") : "30px";

      return (
        <img
          src={icon}
          alt={label}
          width={iconSize}
          height={iconSize}
          style={{
            // filter: `grayscale(${shouldBeWhite ? 0 : 100}%)`,
            filter: iconFilter,
            transition: "filter 0.8s",
            // opacity: shouldBeWhite || !hasError ? 1 : 0.5,
            opacity: iconOpacity,
            // opacity: shouldBeWhite ? 1 : 0.5,
          }}
        />
      );
    }

    if (React.isValidElement(icon)) {
      const iconColor = !endpointAvailable
        ? "#a0a0a0"
        : isActivelyLoading
          ? "#808080" // Dark gray when THIS button is loading
          : isPendingState
            ? "#a0a0a0" // Light gray when waiting
            : isLeagueStateBlocked
              ? "#808080" // Gray when error
              : label === "Rosters"
                ? shouldBeWhite
                  ? "#eaeaea"
                  : "#eaeaea"
                : shouldBeWhite
                  ? "#eaeaea"
                  : "#eaeaea";
      // const iconColor = label === "Rosters" ? (shouldBeWhite ? "#eaeaea" : "#eaeaea") : shouldBeWhite ? "#eaeaea" : "#eaeaea";
      // const iconColor = label === "Rosters" ? (shouldBeWhite ? "#eaeaea" : "#eaeaea") : shouldBeWhite ? "#ffffff" : "#808080";
      // const iconColor = shouldBeWhite ? "#ffffff" : "#808080";
      // Opacity: 1 when actively loading (same as other buttons), 0.5 if pending, otherwise 1
      const iconOpacity = !endpointAvailable ? 0.5 : isActivelyLoading ? 1 : isPendingState ? 0.5 : 1;
      return React.cloneElement(icon, {
        style: {
          fill: "none",
          stroke: iconColor,
          strokeWidth: 0.9,
          transition: "color 0.8s",
          opacity: iconOpacity,
          fontSize: withLargeSize ? 45 : 38,
          ...(icon.props?.style || {}),
        },
      });
    }
    return null;
  };

  // NOTE: Keep UI consistent. Do not render special variants – only change text.
  // The previous early-returns that changed button structure have been commented out intentionally.
  // if (isAnalysisPending && ["Waivers", "Start/Sit", "Team Analysis", "Draft Advice"].includes(label)) { ... }

  // if (isReadyForManualTrigger && ["Waivers", "Start/Sit", "Team Analysis", "Draft Advice"].includes(label)) { ... }

  // Get the number of credits to display in the credits box for agent endpoints
  const agentEndpointCredits = frontEnvNumber("REACT_APP_AGENT_ENDPOINT_CREDITS", 10);
  const isAgentEndpoint = ["Waivers", "Start/Sit", "Team Analysis", "Draft Advice", "Trades",].includes(label);
  const noCreditsInBox = isAgentEndpoint ? agentEndpointCredits : "FREE";
  // Hide credits badge when manual button is ready with cached data (same signal as gradient)
  const shouldHideCreditsBadge = manualReadyWithData;

  return (
    <Button
      variant="contained"
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        // backgroundColor: backgroundColor,
        // Ready/enabled: use loaderGradient (#555→#111) so cache hits never flash flat #555→#333
        background: backgroundColor === initialColor ? `linear-gradient(180deg, ${gradientTopColor}, ${gradientBottomColor})` : loaderGradient,
        // backgroundColor: "transparent",
        borderRadius: "8px",
        // border: "1px solid #bbbbbb",
        // transition: "background-color 0.3s",
        border: "none",
        p: 0,
        transition: "background 0.3s",
        height: "70px",
        pointerEvents: isLoading || !endpointAvailable ? "none" : "auto",
        // opacity: isDisabled ? 0.5 : 1,
        "&:hover": {
          // backgroundColor: backgroundColor,
          background: backgroundColor === initialColor ? `linear-gradient(180deg, ${gradientTopColor}, ${gradientBottomColor})` : loaderGradient,
          backgroundColor: "transparent",

        },
        // CRITICAL: Override Material-UI's default disabled styles
        // "&.Mui-disabled": {
        //   backgroundColor: backgroundColor + " !important",
        // },
      }}
      onClick={handleClick}
      // Manual buttons:
      // - disabled while super analysis is running
      // - disabled while endpoint is starting (loading) until first text_chunk arrives (isGeneratingResult === true)
      // - disabled while thought/status phase is active (isThoughtPhaseActive)
      // - enabled when ready for manual trigger or streaming has produced content
      disabled={
        // isManualLabel ? isAnalysisPending || (manualLoading && !streamHasFirstText) || (isStreamActiveForThisTeam && !streamHasFirstText) : isDisabled || (hasError && hasRetried)
        isManualLabel
          ? isPermanentError || isDisabled || isAnalysisPending || manualStreamBlocksClick
          : label === "Trades"
            ? isPermanentError || isDisabled || isAnalysisPending || (hasError && hasRetried)
            : isDisabled || (hasError && hasRetried)
      }
    >
      {/* Loader bar */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          // PRECISE: Force progress to 0 when showing "Analyzing..." for manual buttons, but NOT on permanent error (isPermanentError keeps loader at 100% so the gradient matches peers like News/Rosters/Trades).
          width: `${useDisabledGradient ? 100 : (isManualLabel && isAnalysisPending && !isPermanentError) ? 0 : progress}%`,
          background: loaderGradient,
          // borderRight: progress > 0 ? `1px solid #2a2a2a` : "none",
          zIndex: 1,
          // When progress reaches 100%, fade the loader out quickly so the button gradient is fully visible.
          // opacity: progress >= 100 ? 0 : 1,
          transition:
            progress < 100
              ? isManualLabel && typeof manualProgressTarget === "number"
                ? "width 0.6s linear"
                : "none"
              : "opacity 0.15s ease-out",
        }}
      />
      <Box sx={buttonContentRowGridSx}>
        {showRetryButton ? (
          <Box sx={buttonContentInnerSx}>
            <Box sx={buttonContentIconSlotSx}>
              {hasRetried ? (
                renderIcon(false)
              ) : (
                <RefreshIcon
                  sx={{
                    width: "30px",
                    height: "30px",
                    color: hasRetried ? "#666666" : "#ffffff",
                  }}
                />
              )}
            </Box>
            <Typography
              sx={{
                ...buttonContentLabelSx,
                color: hasRetried ? "#666666" : "#ffffff",
                // whiteSpace: "nowrap",
                lineHeight: 1.3,
              }}
            >
              {/* {hasRetried ? label + " failed" : "Retry " + label} */}
              {hasRetried
                ? label === "Team Analysis"
                  ? "Analysis failed"
                  : label === "Draft Advice"
                    ? "Draft failed"
                    // : label === "Rookies Rank"
                    // ? "Rookies failed"
                    : label === "Rankings"
                      ? "Rankings failed"
                      : `${label} failed`
                : "Retry " +
                (label === "Team Analysis"
                  ? "Analysis"
                  : label === "Draft Advice"
                    ? "Draft"
                    // : label === "Rookies Rank"
                    //   ? "Rookies"
                    : label === "Rankings"
                      ? "Rankings"
                      : label)}
            </Typography>
          </Box>
        ) : (
          <Box sx={buttonContentInnerSx}>
            <Box sx={buttonContentIconSlotSx}>
              {renderIcon(label === "Start/Sit" || label === "Trades" || label === "Rosters" || label === "Draft Advice")}
            </Box>
            <Typography
              sx={{
                ...buttonContentLabelSx,
                // Keep original styling, but force white text for manual buttons when they are ready to click
                // color:
                //   isManualLabel && isReadyForManualTrigger
                //     ? "#ffffff"
                //     : label === "Rosters"
                //     ? shouldBeWhite
                //       ? "#eaeaea"
                //       : "#ffffff"
                //     : shouldBeWhite && !hasError
                //     ? "#ffffff"
                //     : "#ffffff",
                // whiteSpace: "nowrap",
                // lineHeight: "1.2",
                color: !endpointAvailable
                  ? "#808080"
                  : isLoading
                    ? "#808080" // Gray when loading
                    : isPendingState
                      ? "#808080" // Gray when analyzing
                      : isLeagueStateBlocked
                        ? "#808080" // Gray when error / sync required
                        : isManualLabel && isReadyForManualTrigger
                          ? "#ffffff"
                          : label === "Rosters"
                            ? shouldBeWhite
                              ? "#eaeaea"
                              : "#ffffff"
                            : shouldBeWhite && !hasError
                              ? "#ffffff"
                              : "#ffffff",
                // color: isManualLabel && isReadyForManualTrigger ? "#ffffff" : label === "Rosters" ? (shouldBeWhite ? "#eaeaea" : "#808080") : shouldBeWhite ? "#ffffff" : "#808080",
                transition: "color 0.8s",
                // paddingBottom: label === "Start/Sit" || label === "Trades" ? "10px" : label === "Rosters" ? "8px" : "0",
                // marginTop: label === "Rosters" ? "-10px" : label === "News" ? "1px" : 0,
                // lineHeight: label === "Rosters" ? 1.5 : 1.1,
                lineHeight: 1.2,
                // fontSize: buttonLabelLines ? "13px" : "13.5px",
                fontSize: "13px",
                fontWeight: buttonLabelLines ? 400 : 700,
                // whiteSpace: 'pre-line',
              }}
            >
              {
                // Only change the text (same style, no loader)
                // buttonLabelText
                buttonLabelLines ? (
                  <>
                    <Box component="span" sx={buttonContentTwoLinePrimarySx}>
                      {buttonLabelLines[0]}
                    </Box>
                    <Box component="span" sx={buttonContentTwoLineSecondarySx}>
                      {buttonLabelLines[1]}
                    </Box>
                  </>
                ) : (
                  buttonLabelText
                )
                // isPermanentError ? (label === "Team Analysis" ? "Failed" : label === "Rookies Rank" ? "Failed" : `Failed`) : label
              }
            </Typography>
          </Box>
        )}
      </Box>
      {/* Bottom right: credits — hidden after successful run/cache for current team */}
      {!shouldHideCreditsBadge && (
        <Box sx={{ position: 'absolute', bottom: -3, left: 0, pointerEvents: 'auto', zIndex: 1 }}>
          <CreditsValueButtons credits={noCreditsInBox} isCreditsAnimating={isCreditsAnimating} creditsBoxRef={creditsBoxRef} isAgentEndpoint={isAgentEndpoint} />
        </Box>
      )}
    </Button>
  );
};

export default AnimatedButton;
